import { BACKEND_KNOWN_HTTP_ERROR_CODES } from "../constants/Constants";
import { INTERNAL_SERVER_ERROR } from "../constants/ErrorMessages";
import { BASE_URL_ENDPOINT_PATH } from "../utils/EndpointsUtils";
import { UserSessionUtils } from "../utils/UserSessionUtils";
import { IBaseApiService } from "./IBaseApiService";

const logRequest = (method: string, url: string, body?: any) => {
  const timestamp = new Date().toISOString();
  const sanitizedBody = body ? maskSensitiveFields(JSON.stringify(body)) : undefined;
  console.log(`[API REQUEST] ${timestamp} | ${method} | ${url}`, sanitizedBody ? sanitizedBody : '');
};

const logResponse = (method: string, url: string, status: number, duration: number) => {
  const timestamp = new Date().toISOString();
  const level = status >= 500 ? 'ERROR' : status >= 400 ? 'WARN' : 'INFO';
  console.log(`[API RESPONSE] ${timestamp} | ${level} | ${method} | ${url} | Status: ${status} | Duration: ${duration}ms`);
};

const logError = (method: string, url: string, error: any) => {
  const timestamp = new Date().toISOString();
  console.error(`[API ERROR] ${timestamp} | ${method} | ${url} | Error: ${error.message || error}`);
};

const handleUnauthorized = async (method: string, url: string, response: any) => {
  const timestamp = new Date().toISOString();
  let body = '';
  try {
    body = await response.text();
  } catch (e) {
    body = '[could not read body]';
  }
  console.error(
    `[401 UNAUTHORIZED] ${timestamp} | ${method} | ${url} | ` +
    `Status: ${response.status} | Body: ${body} | ` +
    `Headers: ${JSON.stringify(Object.fromEntries(response.headers.entries()))}`
  );
  console.error('[401] Redirecting to login in 5 seconds... Check the error above.');
  setTimeout(() => {
    UserSessionUtils.clearLocalStorageAndLogoutV2();
  }, 5000);
};

const maskSensitiveFields = (payload: string): string => {
  try {
    let masked = payload;
    masked = masked.replace(/"(password|initialPassword|token|accessToken|refreshToken|apiKey|key)"\s*:\s*"[^"]*"/g, '"$1": "***"');
    return masked;
  } catch {
    return '[error masking payload]';
  }
};

export class BaseApiServiceImpl implements IBaseApiService {
  apiEndpoint: string;
  requestHeaders: {} = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + UserSessionUtils.getBearerToken(),
    deviceId: UserSessionUtils.getDeviceId(),
  };

  multiPartRequestHeaders: {} = {
    // "Content-Type": "multipart/form-data; boundary=??",//- Upload with form data wont work if you specify this header
    Authorization: "Bearer " + UserSessionUtils.getBearerToken(),
    deviceId: UserSessionUtils.getDeviceId(),
  };

  /**
   * This is constructor is used to initialize the API service endpoint to be used for this call.
   *
   * @param apiEndpoint
   */
  constructor(apiEndpoint: string) {
    this.apiEndpoint = BASE_URL_ENDPOINT_PATH + apiEndpoint;
  }

  /**
   * This method is used to make a GET api request to the provided constructor API endpoint.
   *
   * @param queryParameters
   * @returns
   */
  async getRequest(queryParameters: {}) {
    const startTime = Date.now();
    logRequest('GET', this.apiEndpoint);
    return await fetch(this.apiEndpoint + "?" + new URLSearchParams(queryParameters), {
      method: "GET",
      headers: this.requestHeaders,
    }).then((response) => {
      logResponse('GET', this.apiEndpoint, response.status, Date.now() - startTime);
      return response;
    });
  }

  /**
   * This method is used to make a GET api request to the provided constructor API endpoint.
   * This returns a JSON response or redirects to the login screen if a 401 is detected.
   *
   * @param queryParameters
   * @returns
   */
  async getRequestWithJsonResponse(queryParameters: {}) {
    return this.getRequest(queryParameters).then(async (response) => {
      if (response.ok) {
        return response.json();
      } else if (BACKEND_KNOWN_HTTP_ERROR_CODES.includes(response.status)) {
        let data: any = await response.json();
        let errorMessage: string = data?.message ?? data?.responseMessage ?? INTERNAL_SERVER_ERROR;
        logError('GET', this.apiEndpoint, { message: errorMessage });
        throw new TypeError(errorMessage);
      } else if (response.status === 401) {
        await handleUnauthorized('GET', this.apiEndpoint, response);
        throw new TypeError('Unauthorized');
      } else {
        logError('GET', this.apiEndpoint, { message: INTERNAL_SERVER_ERROR });
        throw new TypeError(INTERNAL_SERVER_ERROR);
      }
    });
  }

  /**
   * This method is used to make a POST MULTIPART API request to the provided constructor endpoint.
   *
   * @param requestBody
   * @returns
   */
  async postRequestMultiPart(requestBody: FormData) {
    const startTime = Date.now();
    logRequest('POST', this.apiEndpoint, '[FormData]');
    return await fetch(this.apiEndpoint, {
      method: "POST",
      headers: this.multiPartRequestHeaders,
      body: requestBody,
    }).then((response) => {
      logResponse('POST', this.apiEndpoint, response.status, Date.now() - startTime);
      return response;
    });
  }
  /**
   * This method is used to make a POST API request to the provided constructor endpoint.
   *
   * @param requestBody
   * @returns
   */
  async postRequest(requestBody: {}, requestHeaders: {}) {
    const startTime = Date.now();
    logRequest('POST', this.apiEndpoint, requestBody);
    return await fetch(this.apiEndpoint, {
      method: "POST",
      headers: requestHeaders,
      body: requestBody !== null ? JSON.stringify(requestBody) : "",
    }).then((response) => {
      logResponse('POST', this.apiEndpoint, response.status, Date.now() - startTime);
      return response;
    });
  }

  /**
   * This method is used to make a POST API request to the provided constructor endpoint.
   * This returns a JSON response or redirects to the login screen if a 401 is detected.
   *
   * @param requestBody
   * @returns
   */
  async postRequestWithJsonResponse(requestBody: any) {
    return this.postRequest(requestBody, this.requestHeaders).then(async (response) => {
      if (response.ok) {
        return response.json();
      } else if (BACKEND_KNOWN_HTTP_ERROR_CODES.includes(response.status)) {
        let data: any = await response.json();
        let errorMessage: string = data?.message ?? INTERNAL_SERVER_ERROR;
        logError('POST', this.apiEndpoint, { message: errorMessage, data });
        throw { message: errorMessage, data, status: response.status };
      } else if (response.status === 401) {
        await handleUnauthorized('POST', this.apiEndpoint, response);
        throw new TypeError('Unauthorized');
      } else {
        logError('POST', this.apiEndpoint, { message: INTERNAL_SERVER_ERROR });
        throw new TypeError(INTERNAL_SERVER_ERROR);
      }
    });
  }

  /**
   * This method is used to make a POST API request to the provided constructor endpoint.
   * This returns a JSON response or redirects to the login screen if a 401 is detected.
   *
   * @param requestBody
   * @returns
   */
  async postMultipartWithJsonResponse(requestBody: FormData) {
    return this.postRequestMultiPart(requestBody).then(async (response) => {
      if (response.ok) {
        return response.json();
      } else if (BACKEND_KNOWN_HTTP_ERROR_CODES.includes(response.status)) {
        let data: any = await response.json();
        let errorMessage: string = data?.message ?? INTERNAL_SERVER_ERROR;
        logError('POST', this.apiEndpoint, { message: errorMessage, data });
        throw { message: errorMessage, data, status: response.status };
      } else if (response.status === 401) {
        await handleUnauthorized('POST', this.apiEndpoint, response);
        throw new TypeError('Unauthorized');
      } else {
        logError('POST', this.apiEndpoint, { message: INTERNAL_SERVER_ERROR });
        throw new TypeError(INTERNAL_SERVER_ERROR);
      }
    });
  }

  /**
   * This method is used to obtain a refresh token from the server
   */
  async refreshTokenRequest() {
    let requestBody = { token: UserSessionUtils.getRefreshToken() };
    const startTime = Date.now();
    logRequest('POST', this.apiEndpoint, '[refresh-token]');
    return await fetch(this.apiEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    }).then((response) => {
      logResponse('POST', this.apiEndpoint, response.status, Date.now() - startTime);
      return response;
    });
  }

  /**
   * This method is used to make a PUT API request to the provided constructor endpoint.
   *
   * @param requestBody
   * @returns
   */
  async putRequest(requestBody: {}, requestHeaders: {}) {
    const startTime = Date.now();
    logRequest('PUT', this.apiEndpoint, requestBody);
    return await fetch(this.apiEndpoint, {
      method: "PUT",
      headers: requestHeaders,
      body: requestBody !== null ? JSON.stringify(requestBody) : "",
    }).then((response) => {
      logResponse('PUT', this.apiEndpoint, response.status, Date.now() - startTime);
      return response;
    });
  }

  /**
   * This method is used to make a PUT API request to the provided constructor endpoint.
   * This returns a JSON response or redirects to the login screen if a 401 is detected.
   *
   * @param requestBody
   * @returns
   */
  async putRequestWithJsonResponse(requestBody: any) {
    return this.putRequest(requestBody, this.requestHeaders).then(async (response) => {
      if (response.ok) {
        return response.json();
            } else if (BACKEND_KNOWN_HTTP_ERROR_CODES.includes(response.status)) {
        let data: any = await response.json();
        // Backends return "message" (BaseResponse / ApiErrorResponse); the
        // "responseMessage" fallback is kept for any legacy payloads. Without
        // this, PUT failures were always masked as "Something went wrong".
        let errorMessage: string = data?.message ?? data?.responseMessage ?? INTERNAL_SERVER_ERROR;
        logError('PUT', this.apiEndpoint, { message: errorMessage, data });
        throw { message: errorMessage, data, status: response.status };
      } else if (response.status === 401) {
        await handleUnauthorized('PUT', this.apiEndpoint, response);
        throw new TypeError('Unauthorized');
      } else {
        logError('PUT', this.apiEndpoint, { message: INTERNAL_SERVER_ERROR });
        throw new TypeError(INTERNAL_SERVER_ERROR);
      }
    });
  }

  async deleteRequest(requestHeaders: {}) {
    const startTime = Date.now();
    logRequest('DELETE', this.apiEndpoint);
    return await fetch(this.apiEndpoint, {
      method: "DELETE",
      headers: requestHeaders,
    }).then((response) => {
      logResponse('DELETE', this.apiEndpoint, response.status, Date.now() - startTime);
      return response;
    });
  }

  async deleteRequestWithJsonResponse(requestHeaders: {}) {
    return this.deleteRequest(requestHeaders).then(async (response) => {
      if (response.ok) {
        return response.json();
            } else if (BACKEND_KNOWN_HTTP_ERROR_CODES.includes(response.status)) {
        let data: any = await response.json();
        // See PUT branch: prefer "message" (the field BaseResponse/ApiErrorResponse
        // actually expose) so deletion errors are surfaced verbatim.
        let errorMessage: string = data?.message ?? data?.responseMessage ?? INTERNAL_SERVER_ERROR;
        logError('DELETE', this.apiEndpoint, { message: errorMessage, data });
        throw { message: errorMessage, data, status: response.status };
      } else if (response.status === 401) {
        await handleUnauthorized('DELETE', this.apiEndpoint, response);
        throw new TypeError('Unauthorized');
      } else {
        logError('DELETE', this.apiEndpoint, { message: INTERNAL_SERVER_ERROR });
        throw new TypeError(INTERNAL_SERVER_ERROR);
      }
    });
  }
}